import * as urlTemplate from 'url-template';
import { compareDesc } from 'date-fns';

import {
  ExchangeSearchParamsSectors, SectorTripNumber, SearchTraveler
} from '@user-history/entities/user-history-item';
import { Travelers } from '@booking/entities/booking-arrangement';
import { PaxType } from '@booking/entities/pax-type';
import { formatDateByRegex } from '@ux/mappers/date.mapper';
import { SearchData } from '@shared/base-search-bar/entities/search-data';
import { formatTravelerDate } from '@booking/mappers/traveler-date.mapper';
import { TripTypes } from '@app/entities/trip-types';
import { AirSearchPayloadTrips } from '@flight-results/entities/airSearchPayload';

const TIME_ZONE_CONST = 'T';

export interface MinMaxCalendarInterface {
  MIN: Date;
  MAX: Date;
}

export function exchangeTripTypeMapper(data: ExchangeSearchParamsSectors): TripTypes {
  return data && data.Trips.length ? isOnlyArrivalSector(data.Trips) ? TripTypes.RETURN : TripTypes.DEPARTURE : null;
}

export function isOnlyArrivalSector(Trips: AirSearchPayloadTrips[]): boolean {
  return Trips.length === 1 && !!Trips.find(trip => trip.SectorNumber === SectorTripNumber.RETURN);
}

export function getOneWayDate(data: ExchangeSearchParamsSectors): Date {
  const departure = data.Trips[0];
  return departure ? formatDateByRegex(formatDate(departure.DepartureDate)) : null;
}

export function getDisabledDate(data: ExchangeSearchParamsSectors): Date {
  const sector = data.exchangeDisabledSector[0];
  return sector ? formatDateByRegex(formatDate(sector.DepartureDate)) : null;
}

export function getDeparturePoint(data: ExchangeSearchParamsSectors): AirSearchPayloadTrips {
  return data ? data.Trips.find(trip => trip.SectorNumber === SectorTripNumber.DEPARTURE) : null;
}

export function getReturnPoint(data: ExchangeSearchParamsSectors): AirSearchPayloadTrips {
  return data ? data.Trips.find(trip => trip.SectorNumber === SectorTripNumber.RETURN) : null;
}

export function getDeparturePointDate(data: ExchangeSearchParamsSectors): Date {
  const departure = getDeparturePoint(data);
  return departure ? formatDateByRegex(formatDate(departure.DepartureDate)) : null;
}

export function getReturnPointDate(data: ExchangeSearchParamsSectors): Date {
  const arrival = getReturnPoint(data);
  return arrival ? formatDateByRegex(formatDate(arrival.DepartureDate)) : null;
}

export function mapExchangeSearchData(data: ExchangeSearchParamsSectors, search: SearchData): string {
  const departurePoint = getDeparturePoint(data);
  const returnPoint = getReturnPoint(data);
  if (departurePoint && returnPoint) {
    return mapForRoundTrip(data, departurePoint, returnPoint, search);
  } else if (departurePoint) {
    return mapForOneWay(data, departurePoint, search);
  } else if (returnPoint) {
    return mapForOneWay(data, returnPoint, search);
  } else return '';
}

export function exchangeOriginalRoute(data: ExchangeSearchParamsSectors): string {
  const departurePoint = getDeparturePoint(data);
  const returnPoint = getReturnPoint(data);
  if (departurePoint && returnPoint) {
    return formatRoute(departurePoint.DeparturePoint, returnPoint.DeparturePoint);
  } else if (departurePoint) {
    return formatRoute(departurePoint.DeparturePoint, departurePoint.ArrivalPoint);
  } else if (returnPoint) {
    return formatRoute(returnPoint.DeparturePoint, returnPoint.ArrivalPoint);
  } else return '';
}

const minAndMaxWithDisabledSector = (
  departurePoint, disabledSector, disabledDepartureDate, departureDate
): MinMaxCalendarInterface => {
  if (departurePoint) {
    const compare = compareDesc(departureDate, disabledDepartureDate);
    switch (compare) {
      case -1:
        return { MIN: disabledDepartureDate, MAX: null };
      case 1:
        return { MIN: null, MAX: disabledDepartureDate };
      default:
        if (departurePoint.ReservationIndex < disabledSector.ReservationIndex) {
          return { MIN: null, MAX: disabledDepartureDate };
        } else {
          return { MIN: disabledDepartureDate, MAX: null }; 
        }
    }
  } else {
    const today = new Date();
    const minDate = disabledDepartureDate < today ? null : disabledDepartureDate;
    return { MIN: minDate, MAX: null };
  }
}

export function exchangeMinAndMaxCalendar(
  data: ExchangeSearchParamsSectors,
): MinMaxCalendarInterface {
  const departurePoint = getDeparturePoint(data);
  const returnPoint = getReturnPoint(data);
  const disabledSector = data.exchangeDisabledSector.length ? data.exchangeDisabledSector[0] : null;
  const disabledDepartureDate = disabledSector && new Date(formatStringDateByRegex(formatDate(disabledSector.DepartureDate)));
  const departureDate = departurePoint && new Date(formatStringDateByRegex(formatDate(departurePoint.DepartureDate)));
  if (departurePoint && returnPoint) {
    return { MIN: null, MAX: null };
  } else if (disabledSector && (departurePoint || returnPoint)) {
    return minAndMaxWithDisabledSector(departurePoint, disabledSector, disabledDepartureDate, departureDate)
  } else {
    return { MIN: null, MAX: null };
  }
}

function mapForOneWay(data: ExchangeSearchParamsSectors, departurePoint: AirSearchPayloadTrips, search: SearchData): string {
  const isOnlyArrival = isOnlyArrivalSector(data.Trips);
  const evalDate = isOnlyArrival ? search.arrivalDate : search.departureDate;
  const evalOriginalDate = isOnlyArrival ? data.originalArrival : data.originalDeparture;
  return urlTemplate
    .parse('{itinerary_id}/{reservation_id}/{departure}/{arrival}/{departure_sec_number}/{departure_sector_change}' +
      '/{departure_date}/{adults}/{children}/{infants}/{baggage_included}/{original_departure_date}/{original_route}' +
      '/{original_departure_date_time}' +
      '/{with_assistance_and_longer_duration}/{with_assistance_and_shorter_or_equal_duration}' +
      '/{route_change_is_disabled}'
    )
    .expand({
      itinerary_id: data.TravelItineraryId,
      reservation_id: data.AirReservationId,
      departure: search.departurePlace.code,
      arrival: search.arrivalPlace.code,
      departure_date: formatTravelerDate(evalDate),
      adults: getPaxTypeNumber(data.Passengers, PaxType.ADULTS),
      children: getPaxTypeNumber(data.Passengers, PaxType.CHILDREN),
      infants: getPaxTypeNumber(data.Passengers, PaxType.INFANTS),
      baggage_included: !!data.baggageIncluded,
      departure_sector_change: true,
      departure_sec_number: departurePoint.SectorNumber,
      original_departure_date: formatTravelerDate(new Date(formatStringDateByRegex(evalOriginalDate))),
      original_route: data.originalRoute,
      original_departure_date_time: data.originalDepartureDateTime,
      with_assistance_and_longer_duration: !!data.exchangeWithAssistanceAndLongerDuration,
      with_assistance_and_shorter_or_equal_duration: !!data.exchangeWithAssistanceAndShorterOrEqualDuration,
      route_change_is_disabled: !!data?.RouteChangeIsDisabled,
    });
}

function mapForRoundTrip(data: ExchangeSearchParamsSectors, departurePoint: AirSearchPayloadTrips,
  returnPoint: AirSearchPayloadTrips, search: SearchData): string {
  return urlTemplate.
    parse('{itinerary_id}/{reservation_id}/{departure}/{arrival}/{departure_sec_number}/{departure_sector_change}'
      + '/{departure_date}/{arrival_sec_number}/{arrival_sector_change}/{arrival_date}/{adults}/{children}/{infants}'
      + '/{baggage_included}/{original_departure_date}/{original_arrival_date}/{original_route}'
      + '/{original_departure_date_time}/{original_arrival_date_time}'
      + '/{with_assistance_and_longer_duration}/{with_assistance_and_shorter_or_equal_duration}'
      + '/{route_change_is_disabled}'
    )
    .expand({
      itinerary_id: data.TravelItineraryId,
      reservation_id: data.AirReservationId,
      departure: search.departurePlace.code,
      arrival: search.arrivalPlace.code,
      arrival_date: formatTravelerDate(search.arrivalDate),
      departure_date: formatTravelerDate(search.departureDate),
      adults: getPaxTypeNumber(data.Passengers, PaxType.ADULTS),
      children: getPaxTypeNumber(data.Passengers, PaxType.CHILDREN),
      infants: getPaxTypeNumber(data.Passengers, PaxType.INFANTS),
      baggage_included: !!data.baggageIncluded,
      departure_sec_number: departurePoint.SectorNumber,
      departure_sector_change: true,
      arrival_sector_change: true,
      arrival_sec_number: returnPoint.SectorNumber,
      original_departure_date: formatTravelerDate(new Date(formatStringDateByRegex(data.originalDeparture))),
      original_arrival_date: formatTravelerDate(new Date(formatStringDateByRegex(data.originalArrival))),
      original_route: data.originalRoute,
      original_departure_date_time: data.originalDepartureDateTime,
      original_arrival_date_time: data.originalArrivalDateTime,
      with_assistance_and_longer_duration: !!data.exchangeWithAssistanceAndLongerDuration,
      with_assistance_and_shorter_or_equal_duration: !!data.exchangeWithAssistanceAndShorterOrEqualDuration,
      route_change_is_disabled: !!data?.RouteChangeIsDisabled,
    });
}

export function formatDate(date: string): string {
  const indexTimeZone = date.search(TIME_ZONE_CONST);
  return indexTimeZone > 0 ? date.substring(0, indexTimeZone) : date;
}

export function getPaxTypeNumber(passengers: Travelers[] | SearchTraveler[] = [], type: PaxType): number {
  return passengers.filter(pax => pax.PaxType === type).length;
}

export function sectorIsChange(searchDate: Date, originalDate: string, originalRoute: string, newRoute: string): boolean {
  if (originalDate && searchDate && originalRoute) {
    const formatStrignOriginalDate = formatStringDateByRegex(originalDate);
    const pountDate = new Date(searchDate);
    const original = new Date(formatStrignOriginalDate);
    pountDate.setHours(0, 0, 0, 0);
    original.setHours(0, 0, 0, 0);
    return (pountDate.getTime() !== original.getTime()) || (originalRoute !== newRoute);
  } return false;
}

function formatStringDateByRegex(date: string): string {
  const withTimezone = date ? date.search(TIME_ZONE_CONST) : 0;
  const formatStrignDate = withTimezone > 0 ? date : date.replace(new RegExp(/-/g), '/');
  return formatStrignDate;
}

function formatRoute(departure: string, arrival: string): string {
  return `${departure}-${arrival}`;
}

